/** Weekdays are read from displayed labels, never the histogram's position in the DOM. */
export const POPULAR_DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'] as const;
export type PopularDay = typeof POPULAR_DAYS[number];
const labels: Record<string, PopularDay> = {
  sunday: 'Sun', monday: 'Mon', tuesday: 'Tue', wednesday: 'Wed', thursday: 'Thu', friday: 'Fri', saturday: 'Sat',
};

export function popularDay(label: string): PopularDay | null {
  const name = label.trim().toLowerCase().replace(/^histogram showing popular times on /, '').replace(/s$/, '');
  return labels[name] ?? null;
}

/** Reject a malformed day in full; zero is a measurement and a missing hour remains absent. */
export function popularHistogram(dayLabel: string, rawLabels: string[]) {
  const day = popularDay(dayLabel);
  if (!day || !rawLabels.length) return null;
  const seen = new Set<number>();
  const hours: Array<[number, number]> = [];
  for (const label of rawLabels) {
    const match = label.match(/(?:^|\s)(\d{1,3})% busy at (\d{1,2})(?::(\d{2}))?\s?(am|pm)\b/i);
    if (!match) return null;
    const percent = Number(match[1]), clock = Number(match[2]);
    const hour = clock % 12 + (match[4]!.toLowerCase() === 'pm' ? 12 : 0);
    if (percent > 100 || clock < 1 || clock > 12 || (match[3] && match[3] !== '00') || seen.has(hour)) return null;
    seen.add(hour);
    hours.push([hour, percent]);
  }
  return { day, hours: hours.sort((a, b) => a[0] - b[0]), rawLabels };
}

export function popularCollectionStatus(days: Partial<Record<PopularDay, Array<[number, number]>>>) {
  const captured = POPULAR_DAYS.filter(day => days[day]?.length);
  return captured.length === 7 ? 'complete' : captured.length ? 'partial' : 'chart_unverified';
}

type DayEvidence = { day: PopularDay; displayedDay: string; rawLabels: string[]; capturedAt: string };
export type PopularAttempt = {
  placeId: string; name: string; attemptedAt?: string; status: string;
  days: Partial<Record<PopularDay, Array<[number, number]>>>; evidence?: DayEvidence[];
  overnightHours?: Partial<Record<PopularDay, Array<[number, number]>>>;
  [key: string]: unknown;
};
type PopularResult = PopularAttempt & { attempts?: PopularAttempt[] };

/** A failed retry is still a failed attempt; it cannot erase an earlier verified day. */
export function mergePopularAttempt(previous: PopularResult | undefined, attempt: PopularAttempt) {
  const days = { ...previous?.days, ...attempt.days };
  const replaced = new Set(Object.keys(attempt.days));
  const overnightHours = { ...previous?.overnightHours };
  for (const day of POPULAR_DAYS) if (replaced.has(day)) delete overnightHours[day];
  Object.assign(overnightHours, attempt.overnightHours);
  const evidence = [...(previous?.evidence ?? []).filter(item => !replaced.has(item.day)), ...(attempt.evidence ?? [])];
  const priorAttempts = previous?.attempts ?? (previous ? [{ ...previous }] : []);
  return { ...attempt, days, evidence, ...(previous?.overnightHours || attempt.overnightHours ? { overnightHours } : {}), profileStatus: popularCollectionStatus(days), attempts: [...priorAttempts.filter(prior => prior.attemptedAt !== attempt.attemptedAt), { ...attempt, days: { ...attempt.days }, evidence: [...(attempt.evidence ?? [])] }] };
}
